<template>
  <div
    class="rs-spinbutton overflow-hidden w-100"
    :class="inputClasses"
  >
    <label
      v-if="label"
      :for="id"
    >{{ label }}
      <sup
        v-if="required"
        class="mt-2"
      >*</sup>
    </label>
    <BInputGroup
      :id="id"
    >
      <div
        class="d-flex align-items-center p-1 w-100"
        style="border: 1px #d0cfcf solid; border-radius: 5px;"
      >
        <div
          class="stepper-buttons"
          style="border-right: 1px #d0cfcf solid;"
          @click="onInputChange((validValue - step))"
        >
          <RsIcon icon="minus" />
        </div>
        <input
          :id="`${id}-field`"
          type="number"
          style="border: 0; text-align: center; width: 100%"
          :value="validValue === 0 && isBlank ? validValue : parseFloat(`${validValue || min}`)"
          :max="max"
          :min="min"
          :step="step"
          :required="required"
          :disabled="disabled || isUnlocked == false"
          :readonly="readonly"
          :placeholder="placeholder"
          @change="onInputChange($event.target.value)"
        >
        <div
          class="stepper-buttons"
          style="border-left: 1px #d0cfcf solid;"
          @click="onInputChange((validValue + step))"
        >
          <RsIcon icon="plus" />
        </div>
      </div>
    </BInputGroup>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsFormSpinButton',

  props: {
    rules: [String, Object],

    id: {
      type: String,
      default: '',
    },
    label: String,
    inline: {
      type: Boolean,
      default: false,
    },
    placeholder: String,
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 9999,
    },
    step: {
      type: Number,
      default: 1,
    },
    wrap: {
      type: Boolean,
      default: false,
    },
    value: [String, Number],
    imported: {
      type: Boolean,
      default: false,
    },
    isBlank: {
      type: Boolean,
      default: false,
    },
    /**
     * Color variants:  success | warning | error | error-alt
     *
     */
    variant: {
      type: String,
      validator: (prop: string) => [
        'success',
        'warning',
        'error',
        'error-alt',
      ].includes(prop),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
      validValue: 0 as number,
    };
  },
  computed: {
    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--success': this.variant === 'success',
        'rs-input--warning': this.variant === 'warning',
        'rs-input--error': this.variant === 'error',
        'rs-input--error-alt': this.variant === 'error-alt',
        'rs-input--unlocked': this.isUnlocked,
      };
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (this.isBlank && newValue === null) this.validValue = 0;
        else if (this.isInputValid(newValue)) this.validValue = newValue;
        else this.$emit('input', this.validValue);
        this.$forceUpdate();
      },
    },
  },
  mounted() {
    // add click outside event listener
    const selfElement = document.getElementById(this.id);
    document.addEventListener('click', (event) => {
      const isClickInsideElement = selfElement?.contains(event.target as Node);
      if (!isClickInsideElement) {
        this.triggerFocusout();
      }
    });
  },
  methods: {
    onInputChange(val: number | string) {
      this.triggerFocusin();
      const value = (typeof (val) !== 'number' ? Number(val) : val);
      if (val === '' && this.isBlank) this.$emit('input', null);
      else if (this.isInputValid(value)) this.$emit('input', value);
      else this.$emit('input', this.validValue);
      this.$forceUpdate();
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    isInputValid(val: number): boolean {
      return (val % this.step === 0
          && val <= this.max
          && val >= this.min);
    },
    triggerFocusin(): void {
      const element = document.getElementById(`${this.id}-field`);
      if (element) {
        const focusinEvent = new Event('focusin', { bubbles: true });
        element.dispatchEvent(focusinEvent);
      }
    },
    triggerFocusout(): void {
      const element = document.getElementById(`${this.id}-field`);
      if (element) {
        const focusoutEvent = new Event('focusout', { bubbles: true });
        element.dispatchEvent(focusoutEvent);
      }
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  margin: 0;
}
input[type=number] {
  -moz-appearance: textfield;
}
.rs-spinbutton {
  margin-bottom: 1rem;

  .stepper-buttons {
    width: 40px;
    cursor: pointer;
    text-align: center;
    transition: 0.2s;
    &:hover {
      border-radius: 3px;
      background-color: #ece7e7;
      border-color: #ece7e7 !important;
    }
  }

  .form-control {
    height: 40px;
    padding: 0.5rem 0.5rem 0.5rem 0.5rem;
  }

}
</style>
